import type {
  Availability,
  AvailabilityDay,
  AvailabilityTimeSlot,
  WorkFrequency,
  WorkPreferences,
  WorkType,
} from '@teentack/shared';

/**
 * Labels and conversion for the two JSON columns on `teens`, kept apart from
 * the component that renders them so both the signup wizard and the profile
 * page can import the vocabulary without dragging a component along.
 */

export const DAY_LABELS: Record<AvailabilityDay, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const TIME_SLOT_LABELS: Record<AvailabilityTimeSlot, string> = {
  morning: 'Mornings',
  afternoon: 'Afternoons',
  evening: 'Evenings',
};

export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  one_time: 'One-off gigs',
  ongoing: 'Ongoing work',
};

export const WORK_FREQUENCY_LABELS: Record<WorkFrequency, string> = {
  weekly: 'Weekly',
  biweekly: 'Every other week',
  monthly: 'Monthly',
  flexible: 'Flexible',
};

/** The flat form state behind both JSON columns. */
export interface AvailabilityValue {
  days: AvailabilityDay[];
  timeSlots: AvailabilityTimeSlot[];
  notes: string;
  workTypes: WorkType[];
  frequency: WorkFrequency | '';
}

export const EMPTY_AVAILABILITY: AvailabilityValue = {
  days: [],
  timeSlots: [],
  notes: '',
  workTypes: [],
  frequency: '',
};

/** Seeds the form from a stored profile. */
export function availabilityValueFrom(
  availability: Availability | null,
  workPreferences: WorkPreferences | null,
): AvailabilityValue {
  return {
    days: availability?.days ?? [],
    timeSlots: availability?.timeSlots ?? [],
    notes: availability?.notes ?? '',
    workTypes: workPreferences?.type ?? [],
    frequency: workPreferences?.frequency ?? '',
  };
}

/** `undefined` when nothing was filled in, so a PATCH does not clear the row. */
export function toAvailability(value: AvailabilityValue): Availability | undefined {
  const availability: Availability = {
    ...(value.days.length > 0 ? { days: value.days } : {}),
    ...(value.timeSlots.length > 0 ? { timeSlots: value.timeSlots } : {}),
    ...(value.notes.trim() ? { notes: value.notes.trim() } : {}),
  };
  return Object.keys(availability).length > 0 ? availability : undefined;
}

export function toWorkPreferences(value: AvailabilityValue): WorkPreferences | undefined {
  const preferences: WorkPreferences = {
    ...(value.workTypes.length > 0 ? { type: value.workTypes } : {}),
    ...(value.frequency ? { frequency: value.frequency } : {}),
  };
  return Object.keys(preferences).length > 0 ? preferences : undefined;
}
